import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  FiSearch, FiX, FiChevronRight, FiChevronDown, FiLayers, FiTag, FiShield,
  FiGrid, FiPlus, FiGitBranch, FiCornerDownRight, FiCheck, FiMinus, FiMove, FiEye, FiSave, FiClock, FiGift,
} from 'react-icons/fi';
import { loadTable, getTableData, addRow, saveRow, removeRow } from '../db-viewer/data/dataAccess';
import ServiceBoard from './ServiceBoard';
import PromoPanel from './PromoPanel';
import { classifyAccess, EFFECTIVE_LABEL, EffectiveStatus, userEffectiveAccess, isGrantActive } from '../../lib/serviceAccess';
import { confirmDiscardChanges, setFormDirty } from '../../lib/unsavedChanges';
import './RelationshipWorkbench.css';

// =============================================
// Relationship Workbench — the landing view of Roles & Permissions.
// One screen, three lenses (Service / Plan / Role). Pick a subject, then wire
// its relationships. Inspired by Manage Folders: a cascade drill-down for the
// service tree + chip "rails" (the Owners-chip analog) that write straight to
// the link tables (site_service_plan_access, user_rights).
//
//   Service lens → pick a service via cascade → set its plans + roles.
//   Plan lens    → pick a plan → set which services it unlocks (per-plan column).
//   Role lens    → pick a role → toggle which services it can reach.
// =============================================

interface ServiceRow { service_id: number; service_name: string; parent_service_id: number | null; is_deleted?: number; is_subscribable?: number; }
interface PlanRow { plan_id: number; plan_name: string; monthly_price: number; is_default?: number; is_deleted?: number; }
interface AccessRow { plan_access_id: number; plan_id: number; service_id: number; access_level: string; max_usage?: number | null; is_deleted?: number; }
interface RoleRow { user_type_id: number; user_type: string; hierarchy_level: number; is_active?: number; }
interface UserRow { user_id: number; user_type_id: number; plan_id?: number | null; first_name?: string; last_name?: string; screen_name?: string; email?: string; is_deleted?: number; }
interface GrantRow { grant_id: number; user_id: number; service_id: number; access_level: string; valid_from?: string | null; valid_until?: string | null; source?: string; status?: string; is_active?: number; is_deleted?: number; }
interface RightRow { user_admin_right_id: number; user_type_id: number; admin_service_id: number; service_id?: number | null; service_name: string; status: string; is_active?: number; }

const LEVELS = ['none', 'read', 'limited', 'full'] as const;
type Level = typeof LEVELS[number];
const LEVEL_LABEL: Record<Level, string> = { none: '—', read: 'Read', limited: 'Limited', full: 'Full' };

type Lens = 'service' | 'plan' | 'role';
const LENS_HINT: Record<Lens, string> = {
  service: 'Drill the service tree, then wire its plans & roles below.',
  plan: 'Pick a plan, then set the access level of every service it unlocks.',
  role: 'Pick a role, then toggle which services it can reach.',
};

const RelationshipWorkbench: React.FC = () => {
  // ---- data ----
  const [services, setServices] = useState<ServiceRow[]>(() => getTableData('site_services') as ServiceRow[]);
  const [plans, setPlans] = useState<PlanRow[]>(() => getTableData('site_service_plans') as PlanRow[]);
  const [access, setAccess] = useState<AccessRow[]>(() => getTableData('site_service_plan_access') as AccessRow[]);
  const [roles, setRoles] = useState<RoleRow[]>(() => getTableData('user_type') as RoleRow[]);
  const [rights, setRights] = useState<RightRow[]>(() => getTableData('user_rights') as RightRow[]);
  const [users, setUsers] = useState<UserRow[]>(() => getTableData('users') as UserRow[]);
  const [grants, setGrants] = useState<GrantRow[]>(() => getTableData('user_service_grant') as GrantRow[]);
  const [promos, setPromos] = useState<any[]>(() => getTableData('site_service_promos'));
  const [userPromos, setUserPromos] = useState<any[]>(() => getTableData('user_promo'));
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');

  useEffect(() => {
    Promise.all([
      loadTable('site_services'),
      loadTable('site_service_plans'),
      loadTable('site_service_plan_access'),
      loadTable('user_type'),
      loadTable('user_rights'),
      loadTable('users'),
      loadTable('user_service_grant'),
      loadTable('site_service_promos'),
      loadTable('user_promo'),
    ]).then(([s, p, a, r, ur, u, g, pr, up]) => {
      setServices(s as ServiceRow[]);
      setPlans(p as PlanRow[]);
      setAccess(a as AccessRow[]);
      setRoles(r as RoleRow[]);
      setRights(ur as RightRow[]);
      setUsers(u as UserRow[]);
      setGrants(g as GrantRow[]);
      setPromos(pr);
      setUserPromos(up);
    }).catch(e => setError(e?.message || 'Failed to load'));
  }, []);

  // Re-pull promo ledger + grants after a promo action (redeem may create a grant).
  const reloadPromos = useCallback(async () => {
    try {
      const [up, g] = await Promise.all([loadTable('user_promo'), loadTable('user_service_grant')]);
      setUserPromos(up);
      setGrants(g as GrantRow[]);
    } catch (e: any) { setError(e?.message || 'Failed to reload'); }
  }, []);

  // ---- derived ----
  /* eslint-disable eqeqeq */
  const topLevel = useMemo(
    () => services.filter(s => s.is_deleted != 1 && s.parent_service_id == null).sort((a, b) => a.service_id - b.service_id),
    [services],
  );
  const childrenOf = useCallback(
    (id: number) => services.filter(s => s.is_deleted != 1 && s.parent_service_id == id).sort((a, b) => a.service_id - b.service_id),
    [services],
  );
  const descendantIds = useCallback((id: number): Set<number> => {
    const out = new Set<number>();
    const walk = (pid: number) => services.filter(s => s.is_deleted != 1 && s.parent_service_id == pid).forEach(c => { if (!out.has(c.service_id)) { out.add(c.service_id); walk(c.service_id); } });
    walk(id);
    return out;
  }, [services]);
  const activePlans = useMemo(
    () => plans.filter(p => p.is_deleted != 1).sort((a, b) => a.plan_id - b.plan_id),
    [plans],
  );
  const activeRoles = useMemo(
    () => roles.filter(r => r.is_active != 0).sort((a, b) => (a.hierarchy_level - b.hierarchy_level) || (a.user_type_id - b.user_type_id)),
    [roles],
  );

  const accessRow = useCallback(
    (planId: number, serviceId: number) => access.find(r => r.is_deleted != 1 && r.plan_id == planId && r.service_id == serviceId),
    [access],
  );
  const levelOf = useCallback(
    (planId: number, serviceId: number): Level => (accessRow(planId, serviceId)?.access_level as Level) || 'none',
    [accessRow],
  );
  // Match by service_id (exact) once backfilled; fall back to name for rows that
  // predate the FK (or local-seed rows that never had a service_id).
  const rightRow = useCallback(
    (typeId: number, serviceId: number, serviceName: string) => rights.find(r =>
      r.user_type_id == typeId && (
        (r.service_id != null && r.service_id == serviceId) ||
        (r.service_id == null && (r.service_name || '').trim().toLowerCase() === serviceName.trim().toLowerCase())
      )),
    [rights],
  );
  const roleHas = useCallback(
    (typeId: number, serviceId: number, serviceName: string) => rightRow(typeId, serviceId, serviceName)?.status === 'Yes',
    [rightRow],
  );
  /* eslint-enable eqeqeq */

  // ---- writes ----
  const cyclePlan = useCallback(async (planId: number, serviceId: number) => {
    const key = `p${planId}:s${serviceId}`;
    const row = accessRow(planId, serviceId);
    const current: Level = (row?.access_level as Level) || 'none';
    const next = LEVELS[(LEVELS.indexOf(current) + 1) % LEVELS.length];
    setBusyKey(key); setError('');
    try {
      if (next === 'none') {
        if (row) {
          await removeRow('site_service_plan_access', row.plan_access_id, 'plan_access_id');
          setAccess(a => a.filter(r => r.plan_access_id !== row.plan_access_id));
        }
      } else if (row) {
        await saveRow('site_service_plan_access', row.plan_access_id, { access_level: next }, 'plan_access_id');
        setAccess(a => a.map(r => r.plan_access_id === row.plan_access_id ? { ...r, access_level: next } : r));
      } else {
        const created = await addRow('site_service_plan_access', {
          plan_id: planId, service_id: serviceId, access_level: next, is_active: 1, is_deleted: 0,
        }, 'plan_access_id') as AccessRow;
        setAccess(a => [...a, created]);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not save access change');
    } finally { setBusyKey(''); }
  }, [accessRow]);

  // maxUsage: pass a number/null to also set the per-service item cap; omit (undefined) to leave it unchanged.
  const setPlanLevel = useCallback(async (planId: number, serviceId: number, next: Level, maxUsage?: number | null) => {
    const row = accessRow(planId, serviceId);
    const usagePatch = maxUsage !== undefined ? { max_usage: maxUsage } : {};
    setBusyKey(`p${planId}:s${serviceId}`); setError('');
    try {
      if (next === 'none') {
        if (row) { await removeRow('site_service_plan_access', row.plan_access_id, 'plan_access_id'); setAccess(a => a.filter(r => r.plan_access_id !== row.plan_access_id)); }
      } else if (row) {
        await saveRow('site_service_plan_access', row.plan_access_id, { access_level: next, ...usagePatch }, 'plan_access_id');
        setAccess(a => a.map(r => r.plan_access_id === row.plan_access_id ? { ...r, access_level: next, ...usagePatch } : r));
      } else {
        const created = await addRow('site_service_plan_access', { plan_id: planId, service_id: serviceId, access_level: next, ...usagePatch, is_active: 1, is_deleted: 0 }, 'plan_access_id') as AccessRow;
        setAccess(a => [...a, created]);
      }
    } catch (e: any) { setError(e?.message || 'Could not save access change'); } finally { setBusyKey(''); }
  }, [accessRow]);

  const maxUsageOf = useCallback((planId: number, serviceId: number): number | null => {
    const r = accessRow(planId, serviceId);
    return r && r.max_usage != null ? Number(r.max_usage) : null;   // eslint-disable-line eqeqeq
  }, [accessRow]);

  const reparentService = useCallback(async (serviceId: number, newParentId: number | null): Promise<boolean> => {
    const svc = services.find(s => s.service_id === serviceId);
    if (!svc) return false;
    // eslint-disable-next-line eqeqeq
    if ((svc.parent_service_id ?? null) == newParentId) return false;          // no-op
    if (newParentId === serviceId) { setError('A service can’t be its own parent.'); return false; }
    if (newParentId != null && descendantIds(serviceId).has(newParentId)) {
      setError('Can’t move a service under one of its own sub-modules.');
      return false;
    }
    setBusyKey(`mv${serviceId}`); setError('');
    const prev = svc.parent_service_id ?? null;
    setServices(s => s.map(r => r.service_id === serviceId ? { ...r, parent_service_id: newParentId } : r));   // optimistic
    try {
      await saveRow('site_services', serviceId, { parent_service_id: newParentId }, 'service_id');
      return true;
    } catch (e: any) {
      setServices(s => s.map(r => r.service_id === serviceId ? { ...r, parent_service_id: prev } : r));         // rollback
      setError(e?.message || 'Could not move service');
      return false;
    } finally { setBusyKey(''); }
  }, [services, descendantIds]);

  const saveUserPlan = useCallback(async (userId: number, planId: number): Promise<boolean> => {
    setBusyKey(`u${userId}`); setError('');
    const prev = users.find(u => u.user_id === userId)?.plan_id ?? null;
    setUsers(us => us.map(u => u.user_id === userId ? { ...u, plan_id: planId } : u));   // optimistic
    try {
      await saveRow('users', userId, { plan_id: planId }, 'user_id');
      return true;
    } catch (e: any) {
      setUsers(us => us.map(u => u.user_id === userId ? { ...u, plan_id: prev } : u));     // rollback
      setError(e?.message || 'Could not assign plan');
      return false;
    } finally { setBusyKey(''); }
  }, [users]);

  const addGrant = useCallback(async (userId: number, serviceId: number, level: Level, days: number | null): Promise<boolean> => {
    const existing = grants.find(g => g.user_id === userId && g.service_id === serviceId && g.is_deleted != 1);   // eslint-disable-line eqeqeq
    const today = new Date();
    const validUntil = days != null ? new Date(today.getTime() + days * 86400000).toISOString().slice(0, 10) : null;
    const payload = { access_level: level, valid_from: today.toISOString().slice(0, 10), valid_until: validUntil, source: 'manual', status: 'active', is_active: 1, is_deleted: 0 };
    setBusyKey(`g${userId}:${serviceId}`); setError('');
    try {
      if (existing) {
        await saveRow('user_service_grant', existing.grant_id, payload, 'grant_id');
        setGrants(gs => gs.map(g => g.grant_id === existing.grant_id ? { ...g, ...payload } : g));
      } else {
        const created = await addRow('user_service_grant', { user_id: userId, service_id: serviceId, ...payload }, 'grant_id') as GrantRow;
        setGrants(gs => [...gs, created]);
      }
      return true;
    } catch (e: any) { setError(e?.message || 'Could not add grant'); return false; }
    finally { setBusyKey(''); }
  }, [grants]);

  const removeGrant = useCallback(async (grantId: number): Promise<void> => {
    setBusyKey(`gx${grantId}`); setError('');
    try {
      await removeRow('user_service_grant', grantId, 'grant_id');
      setGrants(gs => gs.filter(g => g.grant_id !== grantId));
    } catch (e: any) { setError(e?.message || 'Could not remove grant'); }
    finally { setBusyKey(''); }
  }, []);

  const toggleRole = useCallback(async (typeId: number, serviceId: number, serviceName: string) => {
    const key = `r${typeId}:s${serviceId}`;
    const row = rightRow(typeId, serviceId, serviceName);
    setBusyKey(key); setError('');
    try {
      if (row) {
        const next = row.status === 'Yes' ? 'No' : 'Yes';
        // Also backfill service_id on name-matched legacy rows so they become exact.
        const patch: Record<string, any> = { status: next };
        if (row.service_id == null) patch.service_id = serviceId;
        await saveRow('user_rights', row.user_admin_right_id, patch, 'user_admin_right_id');
        setRights(rs => rs.map(r => r.user_admin_right_id === row.user_admin_right_id ? { ...r, ...patch } : r));
      } else {
        // No row for this (role, service) yet — create one as "Yes".
        // admin_service_id has a UNIQUE(user_type_id, admin_service_id) constraint, so
        // pick the next free slot for this role.
        // eslint-disable-next-line eqeqeq
        const used = rights.filter(r => r.user_type_id == typeId).map(r => Number(r.admin_service_id) || 0);
        const nextSvcId = (used.length ? Math.max(...used) : 0) + 1;
        const created = await addRow('user_rights', {
          user_type_id: typeId, admin_service_id: nextSvcId, service_id: serviceId, service_name: serviceName, status: 'Yes', is_active: 1,
        }, 'user_admin_right_id') as RightRow;
        setRights(rs => [...rs, created]);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not save permission change');
    } finally { setBusyKey(''); }
  }, [rightRow, rights]);

  // ---- lens / selection state ----
  const [view, setView] = useState<'wire' | 'board' | 'preview' | 'promos'>('wire');
  const [lens, setLens] = useState<Lens>('service');
  // Guard-then-land: switching the workbench mode or lens must check for unsaved wiring first.
  const switchView = async (v: 'wire' | 'board' | 'preview' | 'promos') => {
    if (v === view) return;
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    setView(v);
  };
  const switchLens = async (l: Lens) => {
    if (l === lens) return;
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    setLens(l);
  };
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggleExpand = useCallback((id: number) => {
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const [selPlanId, setSelPlanId] = useState<number | null>(null);
  const [selRoleId, setSelRoleId] = useState<number | null>(null);
  useEffect(() => { if (selPlanId == null && activePlans.length) setSelPlanId(activePlans[0].plan_id); }, [activePlans, selPlanId]);
  useEffect(() => { if (selRoleId == null && activeRoles.length) setSelRoleId(activeRoles[0].user_type_id); }, [activeRoles, selRoleId]);

  return (
    <div className="rw">
      {error && <div className="rw-error">{error}</div>}

      <div className="rw-lens-bar">
        {view === 'wire' && <>
          <span className="rw-lens-label">View by</span>
          <div className="rw-lens" role="tablist">
            {(['service', 'plan', 'role'] as Lens[]).map(l => (
              <button
                key={l}
                role="tab"
                aria-selected={lens === l}
                className={`rw-lens-btn ${lens === l ? 'active' : ''}`}
                onClick={() => switchLens(l)}
              >
                {l === 'service' && <FiGrid size={14} />}
                {l === 'plan' && <FiTag size={14} />}
                {l === 'role' && <FiShield size={14} />}
                <span style={{ textTransform: 'capitalize' }}>{l}</span>
              </button>
            ))}
          </div>
          <span className="rw-lens-hint">{LENS_HINT[lens]}</span>
        </>}
        {view === 'board' && <span className="rw-lens-hint">Drag a service onto another to re-parent it, or into a plan column to include it.</span>}
        {view === 'preview' && <span className="rw-lens-hint">Effective access = role allows it <b>and</b> the plan includes it. Pick a plan + role to see what a user actually gets.</span>}
        {view === 'promos' && <span className="rw-lens-hint">Issue single-use personal codes to a user, or redeem a public code. A service promo grants the service on redeem.</span>}

        <div className="rw-mode" role="tablist" style={{ marginLeft: 'auto' }}>
          <button role="tab" aria-selected={view === 'wire'} className={`rw-mode-btn ${view === 'wire' ? 'active' : ''}`} onClick={() => switchView('wire')} title="Chip-based wiring">
            <FiLayers size={13} /> Wire
          </button>
          <button role="tab" aria-selected={view === 'board'} className={`rw-mode-btn ${view === 'board' ? 'active' : ''}`} onClick={() => switchView('board')} title="Drag-and-drop board">
            <FiMove size={13} /> Board
          </button>
          <button role="tab" aria-selected={view === 'preview'} className={`rw-mode-btn ${view === 'preview' ? 'active' : ''}`} onClick={() => switchView('preview')} title="Effective-access simulator">
            <FiEye size={13} /> Preview
          </button>
          <button role="tab" aria-selected={view === 'promos'} className={`rw-mode-btn ${view === 'promos' ? 'active' : ''}`} onClick={() => switchView('promos')} title="Issue & redeem promo codes">
            <FiGift size={13} /> Promos
          </button>
        </div>
      </div>

      {view === 'board' && (
        <ServiceBoard
          topLevel={topLevel}
          childrenOf={childrenOf}
          activePlans={activePlans}
          levelOf={levelOf}
          maxUsageOf={maxUsageOf}
          setPlanLevel={setPlanLevel}
          activeRoles={activeRoles}
          roleHas={roleHas}
          toggleRole={toggleRole}
          reparentService={reparentService}
          busyKey={busyKey}
        />
      )}

      {view === 'preview' && (
        <PreviewLens
          activePlans={activePlans}
          activeRoles={activeRoles}
          selPlanId={selPlanId}
          setSelPlanId={setSelPlanId}
          selRoleId={selRoleId}
          setSelRoleId={setSelRoleId}
          topLevel={topLevel}
          childrenOf={childrenOf}
          expanded={expanded}
          toggleExpand={toggleExpand}
          levelOf={levelOf}
          roleHas={roleHas}
          users={users}
          saveUserPlan={saveUserPlan}
          rights={rights}
          access={access}
          grants={grants}
          addGrant={addGrant}
          removeGrant={removeGrant}
          busyKey={busyKey}
        />
      )}

      {view === 'promos' && (
        <PromoPanel
          promos={promos}
          users={users}
          activeRoles={activeRoles}
          services={services}
          userPromos={userPromos}
          onChanged={reloadPromos}
        />
      )}

      {view === 'wire' && (
      <div className="rw-card">
        {lens === 'service' && (
          <ServiceLens
            topLevel={topLevel}
            childrenOf={childrenOf}
            activePlans={activePlans}
            activeRoles={activeRoles}
            levelOf={levelOf}
            cyclePlan={cyclePlan}
            roleHas={roleHas}
            toggleRole={toggleRole}
            services={services}
            setServices={setServices}
            busyKey={busyKey}
            setError={setError}
          />
        )}

        {lens === 'plan' && (
          <PlanLens
            activePlans={activePlans}
            selPlanId={selPlanId}
            setSelPlanId={setSelPlanId}
            topLevel={topLevel}
            childrenOf={childrenOf}
            expanded={expanded}
            toggleExpand={toggleExpand}
            levelOf={levelOf}
            setPlanLevel={setPlanLevel}
            access={access}
            busyKey={busyKey}
          />
        )}

        {lens === 'role' && (
          <RoleLens
            activeRoles={activeRoles}
            selRoleId={selRoleId}
            setSelRoleId={setSelRoleId}
            topLevel={topLevel}
            roleHas={roleHas}
            toggleRole={toggleRole}
            rights={rights}
            busyKey={busyKey}
          />
        )}
      </div>
      )}
    </div>
  );
};

// =============================================
// Shared: access-level legend
// =============================================
const AccessLegend: React.FC = () => (
  <div className="rw-legend">
    <span className="rw-legend-title"><FiLayers size={13} /> Access</span>
    {LEVELS.map(l => <span key={l} className={`rw-legend-chip rw-lvl-${l}`}>{LEVEL_LABEL[l]}</span>)}
  </div>
);

// =============================================
// SERVICE LENS — cascade selector + plan rail + role rail
// =============================================
interface ServiceLensProps {
  topLevel: ServiceRow[];
  childrenOf: (id: number) => ServiceRow[];
  activePlans: PlanRow[];
  activeRoles: RoleRow[];
  levelOf: (planId: number, serviceId: number) => Level;
  cyclePlan: (planId: number, serviceId: number) => void;
  roleHas: (typeId: number, serviceId: number, serviceName: string) => boolean;
  toggleRole: (typeId: number, serviceId: number, serviceName: string) => void;
  services: ServiceRow[];
  setServices: React.Dispatch<React.SetStateAction<ServiceRow[]>>;
  busyKey: string;
  setError: (s: string) => void;
}

const ServiceLens: React.FC<ServiceLensProps> = ({
  topLevel, childrenOf, activePlans, activeRoles, levelOf, cyclePlan, roleHas, toggleRole,
  setServices, busyKey, setError,
}) => {
  const [selTop, setSelTop] = useState<ServiceRow | null>(null);
  const [selSub, setSelSub] = useState<ServiceRow | null>(null);

  const selected = selSub || selTop;
  const subList = selTop ? childrenOf(selTop.service_id) : [];

  const createService = useCallback(async (name: string, parentId: number | null): Promise<ServiceRow | null> => {
    setError('');
    try {
      const code = name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').slice(0, 24) + '_' + Math.floor(Math.random() * 9000 + 1000);
      const created = await addRow('site_services', {
        service_name: name.trim(), parent_service_id: parentId, service_code: code,
        is_subscribable: 1, is_active: 1, is_deleted: 0,
      }, 'service_id') as ServiceRow;
      setServices(s => [...s, created]);
      return created;
    } catch (e: any) {
      setError(e?.message || 'Could not create service');
      return null;
    }
  }, [setServices, setError]);

  return (
    <>
      <CascadeField
        label="Service"
        placeholder="Search a service or type to add…"
        options={topLevel}
        selected={selTop}
        onSelect={(s) => { setSelTop(s); setSelSub(null); }}
        onClear={() => { setSelTop(null); setSelSub(null); }}
        onCreate={async (name) => { const c = await createService(name, null); if (c) { setSelTop(c); setSelSub(null); } }}
      />

      {selTop && (
        <div className="rw-sub">
          <CascadeField
            label={<>Sub-module <span className="rw-in">in “{selTop.service_name}”</span></>}
            placeholder={subList.length ? 'Select a sub-module or type to add…' : 'No sub-modules yet — type to add one'}
            options={subList}
            selected={selSub}
            onSelect={(s) => setSelSub(s)}
            onClear={() => setSelSub(null)}
            onCreate={async (name) => { const c = await createService(name, selTop.service_id); if (c) setSelSub(c); }}
          />
        </div>
      )}

      {!selected && (
        <div className="rw-empty">
          <FiCornerDownRight size={15} /> Pick a service above to see and edit which <b>plans</b> include it and which <b>roles</b> can reach it.
        </div>
      )}

      {selected && (
        <div className="rw-panel">
          <div className="rw-selected-head">
            <span className="rw-selected-name">{selected.service_name}</span>
            {selSub
              ? <span className="rw-selected-path"><FiChevronRight size={12} /> sub-module of {selTop!.service_name}</span>
              : (childrenOf(selected.service_id).length > 0
                  ? <span className="rw-selected-path">{childrenOf(selected.service_id).length} sub-modules</span>
                  : <span className="rw-selected-path">top-level service</span>)}
          </div>

          {/* Plans rail */}
          <div className="rw-rail-section">
            <div className="rw-rail-head">
              <span className="rw-rail-title"><FiTag size={13} /> In plans <span className="rw-rail-sub">— click a chip to cycle access</span></span>
              <AccessLegend />
            </div>
            <div className="rw-rail">
              {activePlans.map(p => {
                const lvl = levelOf(p.plan_id, selected.service_id);
                const key = `p${p.plan_id}:s${selected.service_id}`;
                return (
                  <button
                    key={p.plan_id}
                    className={`rw-chip rw-lvl-${lvl} ${busyKey === key ? 'rw-busy' : ''}`}
                    onClick={() => cyclePlan(p.plan_id, selected.service_id)}
                    title={`${p.plan_name}: ${LEVEL_LABEL[lvl]} (click to change)`}
                  >
                    <span className="rw-chip-name">{p.plan_name}</span>
                    <span className="rw-chip-lvl">{LEVEL_LABEL[lvl]}</span>
                  </button>
                );
              })}
              {!activePlans.length && <span className="rw-none">No plans defined yet.</span>}
            </div>
          </div>

          {/* Roles rail */}
          <div className="rw-rail-section">
            <div className="rw-rail-head">
              <span className="rw-rail-title"><FiShield size={13} /> Available to roles <span className="rw-rail-sub">— click to toggle (writes user_rights by name)</span></span>
            </div>
            <div className="rw-rail">
              {activeRoles.map(r => {
                const on = roleHas(r.user_type_id, selected.service_id, selected.service_name);
                const key = `r${r.user_type_id}:s${selected.service_id}`;
                return (
                  <button
                    key={r.user_type_id}
                    className={`rw-chip rw-role ${on ? 'on' : ''} ${busyKey === key ? 'rw-busy' : ''}`}
                    onClick={() => toggleRole(r.user_type_id, selected.service_id, selected.service_name)}
                    title={`${r.user_type}: ${on ? 'Yes' : 'No'} (click to toggle)`}
                  >
                    {on ? <FiCheck size={13} /> : <FiMinus size={13} />}
                    <span className="rw-chip-name">{r.user_type}</span>
                  </button>
                );
              })}
              {!activeRoles.length && <span className="rw-none">No roles defined yet.</span>}
            </div>
          </div>

          {/* Dependency note */}
          <DependencyNote selTop={selTop} selSub={selSub} childCount={selSub ? 0 : childrenOf(selected.service_id).length} />
        </div>
      )}
    </>
  );
};

const DependencyNote: React.FC<{ selTop: ServiceRow | null; selSub: ServiceRow | null; childCount: number }> = ({ selTop, selSub, childCount }) => {
  if (selSub && selTop) {
    return (
      <div className="rw-dep">
        <FiGitBranch size={14} />
        <span>Dependency: granting <b>{selSub.service_name}</b> to a plan/role only makes sense if its parent <b>{selTop.service_name}</b> is also reachable.</span>
      </div>
    );
  }
  if (childCount > 0) {
    return (
      <div className="rw-dep">
        <FiGitBranch size={14} />
        <span>This service has <b>{childCount} sub-module{childCount > 1 ? 's' : ''}</b>. Removing it from a plan would orphan them — grant the parent first, then refine each child.</span>
      </div>
    );
  }
  return null;
};

// =============================================
// CascadeField — search-or-create dropdown (the Manage Folders pattern, condensed)
// =============================================
interface CascadeFieldProps {
  label: React.ReactNode;
  placeholder: string;
  options: ServiceRow[];
  selected: ServiceRow | null;
  onSelect: (s: ServiceRow) => void;
  onClear: () => void;
  onCreate: (name: string) => void;
}

const CascadeField: React.FC<CascadeFieldProps> = ({ label, placeholder, options, selected, onSelect, onClear, onCreate }) => {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const query = text.trim().toLowerCase();
  const filtered = query
    ? options.filter(o => o.service_name.toLowerCase().includes(query))
    : options;
  const exact = options.some(o => o.service_name.trim().toLowerCase() === query);
  const showCreate = query.length > 0 && !exact;

  if (selected) {
    return (
      <div className="rw-field">
        <div className="rw-field-label">{label}</div>
        <div className="rw-search rw-search-selected">
          <FiSearch className="rw-search-icon" size={15} />
          <span className="rw-search-value">{selected.service_name}</span>
          <button className="rw-clear" onClick={() => { setText(''); onClear(); }} title="Clear" aria-label="Clear selection"><FiX size={15} /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="rw-field" ref={ref}>
      <div className="rw-field-label">{label}</div>
      <div className={`rw-search ${open ? 'rw-search-open' : ''}`}>
        <FiSearch className="rw-search-icon" size={15} />
        <input
          value={text}
          placeholder={placeholder}
          onChange={e => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && (
        <div className="rw-dropdown">
          {filtered.map(o => (
            <button key={o.service_id} className="rw-opt" onClick={() => { onSelect(o); setText(''); setOpen(false); }}>
              {o.service_name}
            </button>
          ))}
          {!filtered.length && !showCreate && <div className="rw-opt-empty">No matches.</div>}
          {showCreate && (
            <button className="rw-opt rw-opt-create" onClick={() => { onCreate(text); setText(''); setOpen(false); }}>
              <FiPlus size={14} /> Add new: “{text.trim()}”
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================
// PLAN LENS — pick a plan, set access for every service (one column)
// =============================================
interface PlanLensProps {
  activePlans: PlanRow[];
  selPlanId: number | null;
  setSelPlanId: (id: number) => void;
  topLevel: ServiceRow[];
  childrenOf: (id: number) => ServiceRow[];
  expanded: Set<number>;
  toggleExpand: (id: number) => void;
  levelOf: (planId: number, serviceId: number) => Level;
  setPlanLevel: (planId: number, serviceId: number, next: Level) => void;
  access: AccessRow[];
  busyKey: string;
}

const PlanLens: React.FC<PlanLensProps> = ({
  activePlans, selPlanId, setSelPlanId, topLevel, childrenOf, expanded, toggleExpand, levelOf, setPlanLevel, access, busyKey,
}) => {
  const plan = activePlans.find(p => p.plan_id === selPlanId) || null;
  // eslint-disable-next-line eqeqeq
  const includedCount = useMemo(() => access.filter(r => r.is_deleted != 1 && r.plan_id == selPlanId && r.access_level !== 'none').length, [access, selPlanId]);

  const renderRow = (svc: ServiceRow, depth: number) => {
    const kids = childrenOf(svc.service_id);
    const isParent = kids.length > 0;
    const isOpen = expanded.has(svc.service_id);
    const lvl = plan ? levelOf(plan.plan_id, svc.service_id) : 'none';
    return (
      <React.Fragment key={svc.service_id}>
        <div className="rw-tree-row">
          <div className="rw-tree-name" style={{ paddingLeft: 6 + depth * 22 }}>
            {isParent
              ? <button className="rw-expand" onClick={() => toggleExpand(svc.service_id)} aria-label={isOpen ? 'Collapse' : 'Expand'}>{isOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}</button>
              : <span className="rw-expand-spacer" />}
            <span>{svc.service_name}</span>
            {isParent && <span className="rw-count">{kids.length}</span>}
          </div>
          <div className="rw-tree-ctrl">
            {LEVELS.map(l => (
              <button
                key={l}
                className={`rw-seg ${lvl === l ? `rw-seg-on rw-lvl-${l}` : ''} ${busyKey === `p${plan?.plan_id}:s${svc.service_id}` ? 'rw-busy' : ''}`}
                onClick={() => plan && setPlanLevel(plan.plan_id, svc.service_id, l)}
                title={`Set ${LEVEL_LABEL[l]}`}
              >
                {LEVEL_LABEL[l]}
              </button>
            ))}
          </div>
        </div>
        {isParent && isOpen && kids.map(k => renderRow(k, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <>
      <div className="rw-field-label">Plan</div>
      <div className="rw-pick-row">
        {activePlans.map(p => (
          <button key={p.plan_id} className={`rw-pick ${selPlanId === p.plan_id ? 'active' : ''}`} onClick={() => setSelPlanId(p.plan_id)}>
            <span className="rw-pick-name">{p.plan_name}</span>
            <span className="rw-pick-meta">{Number(p.monthly_price) === 0 ? 'Free' : `$${Number(p.monthly_price).toFixed(2)}/mo`}</span>
          </button>
        ))}
      </div>

      {plan && (
        <div className="rw-panel">
          <div className="rw-selected-head">
            <span className="rw-selected-name">{plan.plan_name}</span>
            <span className="rw-selected-path">{includedCount} service{includedCount === 1 ? '' : 's'} included</span>
            <span className="rw-rail-sub" style={{ marginLeft: 'auto' }}>Set the access level per service</span>
          </div>
          <div className="rw-tree">{topLevel.map(s => renderRow(s, 0))}</div>
        </div>
      )}
    </>
  );
};

// =============================================
// ROLE LENS — pick a role, toggle which (top-level) services it can reach
// =============================================
interface RoleLensProps {
  activeRoles: RoleRow[];
  selRoleId: number | null;
  setSelRoleId: (id: number) => void;
  topLevel: ServiceRow[];
  roleHas: (typeId: number, serviceId: number, serviceName: string) => boolean;
  toggleRole: (typeId: number, serviceId: number, serviceName: string) => void;
  rights: RightRow[];
  busyKey: string;
}

const RoleLens: React.FC<RoleLensProps> = ({ activeRoles, selRoleId, setSelRoleId, topLevel, roleHas, toggleRole, rights, busyKey }) => {
  const role = activeRoles.find(r => r.user_type_id === selRoleId) || null;
  // eslint-disable-next-line eqeqeq
  const yesCount = role ? rights.filter(r => r.user_type_id == role.user_type_id && r.status === 'Yes').length : 0;

  return (
    <>
      <div className="rw-field-label">Role</div>
      <div className="rw-pick-row">
        {activeRoles.map(r => (
          <button key={r.user_type_id} className={`rw-pick ${selRoleId === r.user_type_id ? 'active' : ''}`} onClick={() => setSelRoleId(r.user_type_id)}>
            <span className="rw-pick-name">{r.user_type}</span>
            <span className="rw-pick-meta">level {r.hierarchy_level}</span>
          </button>
        ))}
      </div>

      {role && (
        <div className="rw-panel">
          <div className="rw-selected-head">
            <span className="rw-selected-name">{role.user_type}</span>
            <span className="rw-selected-path">{yesCount} service{yesCount === 1 ? '' : 's'} allowed</span>
            <span className="rw-rail-sub" style={{ marginLeft: 'auto' }}>Toggle access per service</span>
          </div>
          <div className="rw-rail rw-rail-wrap">
            {topLevel.map(s => {
              const on = roleHas(role.user_type_id, s.service_id, s.service_name);
              return (
                <button
                  key={s.service_id}
                  className={`rw-chip rw-role ${on ? 'on' : ''} ${busyKey === `r${role.user_type_id}:s${s.service_id}` ? 'rw-busy' : ''}`}
                  onClick={() => toggleRole(role.user_type_id, s.service_id, s.service_name)}
                  title={`${s.service_name}: ${on ? 'Yes' : 'No'}`}
                >
                  {on ? <FiCheck size={13} /> : <FiMinus size={13} />}
                  <span className="rw-chip-name">{s.service_name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

// =============================================
// PREVIEW LENS — effective access = role ∩ plan (greys out impossible combos)
// =============================================
interface PreviewLensProps {
  activePlans: PlanRow[];
  activeRoles: RoleRow[];
  selPlanId: number | null;
  setSelPlanId: (id: number) => void;
  selRoleId: number | null;
  setSelRoleId: (id: number) => void;
  topLevel: ServiceRow[];
  childrenOf: (id: number) => ServiceRow[];
  expanded: Set<number>;
  toggleExpand: (id: number) => void;
  levelOf: (planId: number, serviceId: number) => Level;
  roleHas: (typeId: number, serviceId: number, serviceName: string) => boolean;
  users: UserRow[];
  saveUserPlan: (userId: number, planId: number) => Promise<boolean>;
  rights: RightRow[];
  access: AccessRow[];
  grants: GrantRow[];
  addGrant: (userId: number, serviceId: number, level: Level, days: number | null) => Promise<boolean>;
  removeGrant: (grantId: number) => Promise<void>;
  busyKey: string;
}

const STATUS_ICON: Record<EffectiveStatus, React.ReactNode> = {
  allowed: <FiCheck size={12} />,
  'blocked-by-plan': <FiTag size={12} />,
  'blocked-by-role': <FiShield size={12} />,
  off: <FiMinus size={12} />,
};
const STATUS_ORDER: EffectiveStatus[] = ['allowed', 'blocked-by-plan', 'blocked-by-role', 'off'];

function daysLeftLabel(until: string): string {
  const d = Math.ceil((new Date(until + 'T00:00:00').getTime() - Date.now()) / 86400000);
  return d <= 0 ? 'expired' : `${d}d left`;
}

const PreviewLens: React.FC<PreviewLensProps> = ({
  activePlans, activeRoles, selPlanId, setSelPlanId, selRoleId, setSelRoleId,
  topLevel, childrenOf, expanded, toggleExpand, levelOf, roleHas,
  users, saveUserPlan, rights, access, grants, addGrant, removeGrant, busyKey,
}) => {
  const plan = activePlans.find(p => p.plan_id === selPlanId) || null;
  const role = activeRoles.find(r => r.user_type_id === selRoleId) || null;

  const flatServices = useMemo(() => {
    const out: ServiceRow[] = [];
    const walk = (list: ServiceRow[]) => list.forEach(s => { out.push(s); walk(childrenOf(s.service_id)); });
    walk(topLevel);
    return out;
  }, [topLevel, childrenOf]);
  const svcName = (id: number) => flatServices.find(s => s.service_id === id)?.service_name || `#${id}`;

  const [selUserId, setSelUserId] = useState<number | null>(null);
  const [addSvcId, setAddSvcId] = useState<number | ''>('');
  const [addDays, setAddDays] = useState<string>('30');
  const [addLevel, setAddLevel] = useState<Level>('full');
  const activeUsers = useMemo(
    () => users.filter(u => u.is_deleted != 1).sort((a, b) => a.user_id - b.user_id),   // eslint-disable-line eqeqeq
    [users],
  );
  const selUser = activeUsers.find(u => u.user_id === selUserId) || null;
  const userLabel = (u: UserRow) => {
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.screen_name || u.email || `User #${u.user_id}`;
    const roleName = activeRoles.find(r => r.user_type_id == u.user_type_id)?.user_type;   // eslint-disable-line eqeqeq
    return roleName ? `${name} — ${roleName}` : name;
  };
  const onPickUser = (id: number | null) => {
    setSelUserId(id);
    const u = activeUsers.find(x => x.user_id === id);
    if (u) {
      setSelRoleId(u.user_type_id);
      if (u.plan_id != null) setSelPlanId(Number(u.plan_id));   // eslint-disable-line eqeqeq
    }
  };
  // eslint-disable-next-line eqeqeq
  const userPlanMatchesSelection = selUser != null && selUser.plan_id != null && Number(selUser.plan_id) == selPlanId;
  // eslint-disable-next-line eqeqeq
  const userGrants = selUser ? grants.filter(g => g.user_id === selUser.user_id && g.is_deleted != 1) : [];

  // Grant-aware when a user is selected (folds in active per-user grants); otherwise
  // a pure role ∩ plan simulation.
  const statusOf = useCallback((svc: ServiceRow): { status: EffectiveStatus; source: 'grant' | 'plan' | 'none'; grant: any } => {
    if (!plan || !role) return { status: 'off', source: 'none', grant: null };
    if (selUser) {
      const ua = userEffectiveAccess(role.user_type_id, selUser.user_id, plan.plan_id, svc.service_id, svc.service_name, { rights, access, grants });
      return { status: ua.status, source: ua.source, grant: ua.grant };
    }
    return { status: classifyAccess(roleHas(role.user_type_id, svc.service_id, svc.service_name), levelOf(plan.plan_id, svc.service_id)), source: 'plan', grant: null };
  }, [plan, role, selUser, rights, access, grants, roleHas, levelOf]);

  // counts across the whole tree
  const counts = useMemo(() => {
    const c: Record<EffectiveStatus, number> = { allowed: 0, 'blocked-by-plan': 0, 'blocked-by-role': 0, off: 0 };
    const walk = (list: ServiceRow[]) => list.forEach(s => { c[statusOf(s).status]++; walk(childrenOf(s.service_id)); });
    walk(topLevel);
    return c;
  }, [statusOf, childrenOf, topLevel]);

  const renderRow = (svc: ServiceRow, depth: number) => {
    const kids = childrenOf(svc.service_id);
    const isParent = kids.length > 0;
    const isOpen = expanded.has(svc.service_id);
    const { status, source, grant } = statusOf(svc);
    return (
      <React.Fragment key={svc.service_id}>
        <div className={`rw-tree-row rw-prev-row rw-prev-${status}`}>
          <div className="rw-tree-name" style={{ paddingLeft: 6 + depth * 22 }}>
            {isParent
              ? <button className="rw-expand" onClick={() => toggleExpand(svc.service_id)} aria-label={isOpen ? 'Collapse' : 'Expand'}>{isOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}</button>
              : <span className="rw-expand-spacer" />}
            <span>{svc.service_name}</span>
            {isParent && <span className="rw-count">{kids.length}</span>}
          </div>
          <span className="rw-prev-right">
            {source === 'grant' && <span className="rw-grant-badge" title={grant?.valid_until ? `via grant — expires ${grant.valid_until}` : 'via grant'}>grant{grant?.valid_until ? ` · ${daysLeftLabel(grant.valid_until)}` : ''}</span>}
            <span className={`rw-status rw-status-${status}`} title={EFFECTIVE_LABEL[status]}>
              {STATUS_ICON[status]} {EFFECTIVE_LABEL[status]}
            </span>
          </span>
        </div>
        {isParent && isOpen && kids.map(k => renderRow(k, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="rw-card">
      <div className="rw-field-label">Test as user <span className="rw-in">(optional — loads their role + plan)</span></div>
      <div className="rw-userbar">
        <select
          className="rw-user-select"
          value={selUserId ?? ''}
          onChange={e => onPickUser(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Simulate manually…</option>
          {activeUsers.map(u => <option key={u.user_id} value={u.user_id}>{userLabel(u)}</option>)}
        </select>
        {selUser && (
          <button
            className="rw-assign-btn"
            disabled={selPlanId == null || userPlanMatchesSelection || busyKey === `u${selUser.user_id}`}
            onClick={() => selPlanId != null && saveUserPlan(selUser.user_id, selPlanId)}
            title="Persist this plan to the user (users.plan_id)"
          >
            <FiSave size={13} /> {userPlanMatchesSelection ? 'Plan assigned' : `Assign ${plan?.plan_name ?? 'plan'} to user`}
          </button>
        )}
        {selUser && (
          <span className="rw-user-curplan">
            current: <b>{selUser.plan_id != null ? (activePlans.find(p => p.plan_id == Number(selUser.plan_id))?.plan_name ?? `#${selUser.plan_id}`) : 'none'}</b>{/* eslint-disable-line eqeqeq */}
          </span>
        )}
      </div>

      <div className="rw-field-label">Plan</div>
      <div className="rw-pick-row">
        {activePlans.map(p => (
          <button key={p.plan_id} className={`rw-pick ${selPlanId === p.plan_id ? 'active' : ''}`} onClick={() => setSelPlanId(p.plan_id)}>
            <span className="rw-pick-name">{p.plan_name}</span>
            <span className="rw-pick-meta">{Number(p.monthly_price) === 0 ? 'Free' : `$${Number(p.monthly_price).toFixed(2)}/mo`}</span>
          </button>
        ))}
      </div>

      <div className="rw-field-label">Role</div>
      <div className="rw-pick-row">
        {activeRoles.map(r => (
          <button key={r.user_type_id} className={`rw-pick ${selRoleId === r.user_type_id ? 'active' : ''}`} onClick={() => setSelRoleId(r.user_type_id)}>
            <span className="rw-pick-name">{r.user_type}</span>
            <span className="rw-pick-meta">level {r.hierarchy_level}</span>
          </button>
        ))}
      </div>

      {plan && role && (
        <div className="rw-panel">
          <div className="rw-selected-head">
            <span className="rw-selected-name">{role.user_type} on {plan.plan_name}</span>
            <span className="rw-status-legend">
              {STATUS_ORDER.map(s => (
                <span key={s} className={`rw-status rw-status-${s}`}>{STATUS_ICON[s]} {EFFECTIVE_LABEL[s]} <b>{counts[s]}</b></span>
              ))}
            </span>
          </div>
          <div className="rw-tree">{topLevel.map(s => renderRow(s, 0))}</div>
          <div className="rw-dep">
            <FiEye size={14} />
            <span>Rows blocked <b>by plan</b> mean the role would allow it but {plan.plan_name} doesn’t include it — upgrade the plan, add the service to it, or grant the user directly below. Blocked <b>by role</b> means the plan covers it but {role.user_type} isn’t permitted.</span>
          </div>

          {selUser && (
            <div className="rw-grants">
              <div className="rw-rail-title"><FiClock size={13} /> Per-user grants <span className="rw-rail-sub">override this user's plan — adds a service with an optional expiry</span></div>
              <div className="rw-grant-add">
                <select value={addSvcId} onChange={e => setAddSvcId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">Service…</option>
                  {flatServices.map(s => <option key={s.service_id} value={s.service_id}>{s.service_name}</option>)}
                </select>
                <select value={addLevel} onChange={e => setAddLevel(e.target.value as Level)}>
                  {(['full', 'limited', 'read'] as Level[]).map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <input type="number" min={0} placeholder="days (∞)" value={addDays} onChange={e => setAddDays(e.target.value)} title="Days until expiry (blank = no expiry)" />
                <button
                  className="rw-assign-btn"
                  disabled={addSvcId === '' || busyKey === `g${selUser.user_id}:${addSvcId}`}
                  onClick={() => { if (addSvcId !== '') { addGrant(selUser.user_id, Number(addSvcId), addLevel, addDays.trim() === '' ? null : Math.max(0, Math.round(Number(addDays) || 0))); setAddSvcId(''); } }}
                ><FiPlus size={13} /> Grant</button>
              </div>
              <div className="rw-grant-list">
                {userGrants.length === 0 && <span className="rw-none">No grants. Add one above to give this user a service beyond their plan.</span>}
                {userGrants.map(g => (
                  <div key={g.grant_id} className={`rw-grant-chip ${isGrantActive(g) ? '' : 'rw-grant-off'}`}>
                    <span className="rw-grant-svc">{svcName(g.service_id)}</span>
                    <span className="rw-grant-lvl">{g.access_level}</span>
                    <span className="rw-grant-until">{g.valid_until ? daysLeftLabel(g.valid_until) : '∞'}</span>
                    <button className="rw-pill-x" onClick={() => removeGrant(g.grant_id)} title="Remove grant" aria-label="Remove"><FiX size={12} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RelationshipWorkbench;
